import mitt from 'mitt'
import { t } from '@/utils/helpers/translate'

export const discardChangesEventBus = mitt()

/**
 * @description Mixin to treat the discard changes modal
 *
 * Why use this @Mixin ?
 * This mixin provide some ways to control your discard changes modal,
 * also is a good option to prevent the repeated code around the application
 *
 * How to use this @Mixin
 * First, import this mixin in your component and declare it on the `mixins` attribute
 *
 * To start, you will need call the method @initDiscardChangesModal in your @created or @mounted component hook to init the discard changes modal
 * created () {
 *  this.initDiscardChangesModal()
 * }
 *
 * also you can pass your own initial attributes, that are the all options possible and their initial values
 * this.initDiscardChangesModal({
 *   opened: false,
 *   loading: false,
 *   customClass: '',
 *   testId: 'discard-changes-modal',
 *   type: 'caution',
 *   title: 'Discard changes?',
 *   content: `There are some changes in the story which are <br> not saved. Are you sure you want to discard the changes?`,
 *   buttonLabel: 'Discard changes',
 *   watchRoute: true,
 *   ignoredRoutesOnUpdate: [],
 * })
 *
 * also we have the @updateDiscardChangesModal method to update the initial attributes
 *
 * after that you will need to include the <DiscardChangesModal/> in your component template, without any params and the component order does not matter!
 *
 * How the mixin know if has changes?
 * for the mixin to know if has changes, we need a boolean coming from the computed property @discardChangesModalDiff, the suggestion is do a diff checking into changes then return a boolean
 *
 * When the modal will open?
 * when user try to leave the page by clicking on arrow back from browser or clicking in a route change method, the discard changes modal will open if @discardChangesModalDiff and also the @watchRoute are true
 *
 * But when a route update occurs (like changing only the parameters values) the @beforeRouteUpdate hook will be called instead of @beforeRouteLeave ,
 * and he will not try to open the modal if the target route are inside the ignoredRoutesOnUpdate attribute when @beforeRouteUpdate hook occurs
 *
 * also whe have the @watchRoute attribute which controlls if the mixin will watch the route change to show the modal or not, if disabled, the modal will need a manual trigger to be opened, in this mixin we have the methods
 * to treat this, the @handleDiscardChangesModalOpen method and the @handleDiscardChangesModalClose method
 *
 **/

const DEFAULT_ATTRIBUTES = {
  id: 'default',
  opened: false,
  loading: false,
  customClass: 'modal-delete-caution-confirm--hide-close-button',
  testId: 'discard-changes-modal',
  type: 'caution',
  title: t('Discard changes?'),
  content: t(
    `There are some changes which are not saved. Are you sure you want to discard the changes?`,
  ),
  buttonLabel: t('Discard changes'),
  watchRoute: true,
  ignoredRoutesOnUpdate: [],
  ignoredRoutesOnLeave: [],
  ignoredRouteQueryParamOnUpdate: [],
  ignoredRouteQueryParamOnLeave: [],
  allowParentNavigation: true,
}

export default {
  data() {
    return {
      discardChangesModal: {
        attributes: { ...DEFAULT_ATTRIBUTES },
        watchRouteTemporarilyDisabled: false,
      },
    }
  },

  created() {
    discardChangesEventBus.on(
      'discardChangesPrevent',
      this.discardChangesPreventHandler,
    )
  },

  unmounted() {
    discardChangesEventBus.off(
      'discardChangesPrevent',
      this.discardChangesPreventHandler,
    )
  },

  computed: {
    discardChangesModalDiff() {
      return {}
    },

    discardChangesModalWatchRoute() {
      return this.discardChangesModal.attributes.watchRoute
    },

    discardChangesModalIgnoredRoutesOnUpdate() {
      return this.discardChangesModal.attributes.ignoredRoutesOnUpdate
    },

    discardChangesModalIgnoredRoutesOnLeave() {
      return this.discardChangesModal.attributes.ignoredRoutesOnLeave
    },

    discardChangesModalIgnoredRouteQueryParamOnUpdate() {
      return this.discardChangesModal.attributes.ignoredRouteQueryParamOnUpdate
    },

    discardChangesModalIgnoredRouteQueryParamOnLeave() {
      return this.discardChangesModal.attributes.ignoredRouteQueryParamOnLeave
    },

    discardChangesModalAllowParentNavigation() {
      return this.discardChangesModal.attributes.allowParentNavigation
    },
  },

  methods: {
    async discardChangesPromise() {
      if (this.discardChangesModalDiff) {
        this.discardChangesModal.attributes.opened = true

        try {
          await new Promise((resolve, reject) => {
            discardChangesEventBus.on('discardChangesModalClose', () => {
              this.handleDiscardChangesModalClose()

              return reject(new Error())
            })

            discardChangesEventBus.on('discardChangesModalConfirm', () => {
              this.handleDiscardChangesModalConfirm()

              return resolve()
            })
          })
        } finally {
          discardChangesEventBus.off('discardChangesModalClose')
          discardChangesEventBus.off('discardChangesModalConfirm')
        }
      }
    },

    discardChangesPreventHandler(originalCallback) {
      this.discardChangesPromise()
        .then(() => {
          this.watchRouteTemporarilyDisabled = true

          originalCallback()

          this.watchRouteTemporarilyDisabled = false
        })
        .catch(() => {})
    },

    async discardChangesRouteMiddleware(to, from, next) {
      const isRouteByPassed =
        to.meta.discardChangesByPass ||
        !this.discardChangesModalWatchRoute ||
        this.watchRouteTemporarilyDisabled

      const hasParentNavigation =
        this.discardChangesModalAllowParentNavigation &&
        (to.fullPath.includes(from.fullPath) ||
          from.fullPath.includes(to.fullPath))

      if (isRouteByPassed || hasParentNavigation) {
        return next()
      }

      try {
        await this.discardChangesPromise()

        next()
      } catch {
        next(false)
      }
    },

    initDiscardChangesModal(discardChangesModalAttributes) {
      const isSame =
        discardChangesModalAttributes.id ===
        this.discardChangesModal.attributes.id

      const attributesToKeep = isSame
        ? this.discardChangesModal.attributes
        : DEFAULT_ATTRIBUTES

      this.discardChangesModal.attributes = {
        ...attributesToKeep,
        ...discardChangesModalAttributes,
      }

      this.$nextTick(() => {
        this.createComponent()
      })
    },

    destroyDiscardChangesModal() {
      this.discardChangesModal.attributes = { ...DEFAULT_ATTRIBUTES }
      discardChangesEventBus.emit('discardChangesModalDestroy')
      this.createComponent()
    },

    updateDiscardChangesModal(discardChangesModalAttributes) {
      this.discardChangesModal.attributes = {
        ...this.discardChangesModal.attributes,
        ...discardChangesModalAttributes,
      }
    },

    createComponent() {
      discardChangesEventBus.emit(
        'discardChangesModalSync',
        this.discardChangesModal.attributes,
      )
    },

    handleDiscardChangesModalConfirm() {
      this.handleClose()
    },

    handleDiscardChangesModalOpen() {
      this.discardChangesModal.attributes.opened = true
    },

    handleDiscardChangesModalClose() {
      this.handleClose()
    },

    handleClose() {
      this.discardChangesModal.attributes.opened = false
      this.createComponent()
    },

    routeUpdatedByIgnoredParam(to, from, ignoredParams) {
      return Object.entries(from.query).some(([param, value]) => {
        const hasParamChanged = to.query[param] !== value
        return hasParamChanged && ignoredParams.includes(param)
      })
    },

    onRouteUpdate(to, from, next) {
      const ignoredParams =
        this.discardChangesModalIgnoredRouteQueryParamOnUpdate

      if (
        this.discardChangesModalIgnoredRoutesOnUpdate.includes(to.name) ||
        this.routeUpdatedByIgnoredParam(to, from, ignoredParams)
      ) {
        return next()
      }

      return this.discardChangesRouteMiddleware(to, from, next)
    },

    onRouteLeave(to, from, next) {
      const ignoredParams =
        this.discardChangesModalIgnoredRouteQueryParamOnLeave

      if (
        this.discardChangesModalIgnoredRoutesOnLeave.includes(to.name) ||
        this.routeUpdatedByIgnoredParam(to, from, ignoredParams)
      ) {
        return next()
      }

      return this.discardChangesRouteMiddleware(to, from, next)
    },
  },
}
